import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { hasAnyPermission, getStoredUser, logout as authLogout } from '../services/authService';
import { getFullImageUrl } from '../services/api';
import { useSystemSettings } from '../context/SystemSettingsContext';
import { useTranslation } from '../context/LanguageContext';
import { MODULES_BC_ENABLED } from '../config/features';
import './Sidebar.css';

/* ===== 좌측 메뉴 — PNG 시안(C:\tmp\smart_injection_monitor_UX\menu, 펼침 240px / 접힘 60px) =====
 * 사이드바가 화면 전체 높이를 차지하고 맨 위에 로고와 접기/펼치기 버튼이 있다(헤더는 사이드바 오른쪽부터 시작).
 * 메뉴는 "관제"(모니터링·AI) / "관리"(공장·회원·시스템·플랫폼) 두 구역으로 나뉘고, 구역 라벨은 양옆 선이 있는 작은 글자.
 * 펼침: 그룹 행(아이콘·이름·화살표) + 하위 메뉴(왼쪽 세로 안내선, 현재 화면은 카드 강조).
 * 접힘: 아이콘만, 현재 그룹은 사각 강조, 마우스를 올리면 하위 메뉴 툴팁.
 * 맨 아래는 로그인 사용자(이름 첫 글자 아바타 + 이름). 스타일은 sb- 접두어, 색은 사출기 대시보드와 같은 토큰 값. */

/* 페이지마다 MainLayout 을 따로 감싸고 있어서 화면을 이동하면 사이드바가 다시 마운트된다.
 * 열어 둔 그룹을 상태로만 들고 있으면 이동할 때마다 모두 닫혔다가 현재 그룹만 다시 열리며 깜빡인다.
 * 그래서 열린 그룹 목록을 sessionStorage 에 두고 마운트 시 그 값으로 시작한다(사이드바 접힘 여부와 같은 방식). */
const EXPANDED_MENUS_KEY = 'sidebarExpandedMenus';
const readExpandedMenus = () => {
  try {
    const v = JSON.parse(sessionStorage.getItem(EXPANDED_MENUS_KEY));
    return Array.isArray(v) ? v : [];
  } catch (e) {
    return [];
  }
};

const SECTIONS = [
  { key: 'control', labelKey: 'sidebar.section.control' },
  { key: 'admin', labelKey: 'sidebar.section.admin' }
];

/* XSS 방지: 로고 이미지 URL 검증 (Header 에 있던 함수를 로고와 함께 옮김). http(s)·data:image·상대 경로만 허용 */
const sanitizeImageUrl = (url) => {
  if (!url) return '';
  const allowedProtocols = ['http:', 'https:', 'data:'];
  try {
    if (url.startsWith('data:')) return url.startsWith('data:image/') ? url : '';
    const parsed = new URL(url, window.location.origin);
    if (allowedProtocols.includes(parsed.protocol)) return url;
  } catch (e) {
    if (url.startsWith('/') && !url.startsWith('//')) return url;
  }
  return '';
};

function Sidebar({ isExpanded, onToggle }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { settings } = useSystemSettings();
  // MES 좌측 메뉴처럼 여러 그룹을 동시에 펼쳐 둘 수 있도록 열린 메뉴를 목록으로 들고 있는다(세션에 보존).
  const [expandedMenus, setExpandedMenus] = useState(readExpandedMenus);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  useEffect(() => {
    if (!userMenuOpen) return undefined;
    const onDown = (e) => { if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [userMenuOpen]);
  useEffect(() => {
    sessionStorage.setItem(EXPANDED_MENUS_KEY, JSON.stringify(expandedMenus));
  }, [expandedMenus]);
  const [hoveredMenu, setHoveredMenu] = useState(null);

  const user = getStoredUser();
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  const hasMenuPermission = (permissions) => {
    if (!permissions || permissions.length === 0) return true;
    return hasAnyPermission(permissions);
  };

  // 메뉴 항목: title/name 자리에 i18n key 를 보관 후 렌더링 시 t() 로 변환. section 으로 관제/관리 구역을 나눈다
  const allMenuItems = [
    {
      id: 'enterprise',
      section: 'control',
      icon: 'apartment',
      titleKey: 'sidebar.enterprise.title',
      requiredPermissions: ['enterprise.view'],
      submenus: [
        { nameKey: 'sidebar.enterprise.dashboard', path: '/enterprise', permissions: ['enterprise.view'] },
        { nameKey: 'sidebar.enterprise.factoryOverview', path: '/enterprise-factory-overview', permissions: ['enterprise.view'] },
        { nameKey: 'sidebar.enterprise.dataflow', path: '/enterprise-dataflow', permissions: ['enterprise.view'] },
      ]
    },
    {
      id: 'factory',
      section: 'control',
      icon: 'factory',
      titleKey: 'sidebar.factory.title',
      requiredPermissions: ['factory_monitoring.view'],
      submenus: [
        { nameKey: 'sidebar.factory.overview', path: '/factory', permissions: ['factory_monitoring.view'] },
        { nameKey: 'sidebar.factory.machineOverview', path: '/factory-machine-overview', permissions: ['factory_monitoring.view'] },
        { nameKey: 'sidebar.factory.defectHistory', path: '/factory-defect-history', permissions: ['factory_monitoring.view'] },
        { nameKey: 'sidebar.factory.realtimeShot', path: '/realtime-shot-status', permissions: ['factory_monitoring.view'] }
      ]
    },
    {
      id: 'machine',
      section: 'control',
      icon: 'filter_alt',
      titleKey: 'sidebar.machine.title',
      requiredPermissions: ['machine.view'],
      submenus: [
        { nameKey: 'sidebar.machine.dashboard', path: '/machine', permissions: ['machine.view'] },
        { nameKey: 'sidebar.machine.equipmentBoard', path: '/equipment-board', permissions: ['machine.view'] },
      ]
    },
    {
      id: 'ai',
      section: 'control',
      icon: 'text_fields_alt',
      titleKey: 'sidebar.ai.title',
      requiredPermissions: ['ai.view'],
      submenus: [
        { nameKey: 'sidebar.ai.analysis', path: '/ai-analysis', permissions: ['ai.view'] },
        { nameKey: 'sidebar.ai.manage', path: '/ai-manage', permissions: ['ai.manage'] },
      ]
    },
    {
      id: 'moduleb',
      section: 'control',
      icon: 'hub',
      titleKey: 'sidebar.moduleb.title',
      devOnly: true,
      submenus: [
        { nameKey: 'sidebar.moduleb.sync', path: '/module-b/sync' },
        { nameKey: 'sidebar.moduleb.features', path: '/module-b/features' },
        { nameKey: 'sidebar.moduleb.fusion', path: '/module-b/fusion' },
        { nameKey: 'sidebar.moduleb.judgment', path: '/module-b/judgment' },
      ]
    },
    {
      id: 'modulec',
      section: 'control',
      icon: 'precision_manufacturing',
      titleKey: 'sidebar.modulec.title',
      devOnly: true,
      submenus: [
        { nameKey: 'sidebar.modulec.recommend', path: '/module-c/recommend' },
        { nameKey: 'sidebar.modulec.safeguard', path: '/module-c/safeguard' },
      ]
    },
    {
      id: 'management',
      section: 'admin',
      icon: 'room_preferences',
      titleKey: 'sidebar.management.title',
      requiredPermissions: ['factory.view', 'equipment.view'],
      submenus: [
        { nameKey: 'sidebar.management.factoryList', path: '/factory-list', permissions: ['factory.view'] },
        { nameKey: 'sidebar.management.factoryRegister', path: '/factory/register', permissions: ['factory.create'] },
        { nameKey: 'sidebar.management.equipmentList', path: '/equipment-list', permissions: ['equipment.view'] },
        { nameKey: 'sidebar.management.equipmentRegister', path: '/equipment/register', permissions: ['equipment.create'] }
      ]
    },
    {
      id: 'user',
      section: 'admin',
      icon: 'manage_accounts',
      titleKey: 'sidebar.user.title',
      requiredPermissions: ['user.view', 'role.view', 'notice.view'],
      submenus: [
        { nameKey: 'sidebar.user.list', path: '/user', permissions: ['user.view'] },
        { nameKey: 'sidebar.user.register', path: '/user/register', permissions: ['user.create'] },
        { nameKey: 'sidebar.user.role', path: '/role-management', permissions: ['role.view'] },
        { nameKey: 'sidebar.user.notice', path: '/notices', permissions: ['notice.view'] }
      ]
    },
    {
      id: 'system',
      section: 'admin',
      icon: 'settings',
      titleKey: 'sidebar.system.title',
      requiredPermissions: ['system.view'],
      submenus: [
        { nameKey: 'sidebar.system.general', path: '/system?tab=general', permissions: ['system.view'] },
        { nameKey: 'sidebar.system.monitoring', path: '/system?tab=monitoring', permissions: ['system.view'] },
        { nameKey: 'sidebar.system.alarm', path: '/system?tab=alarm', permissions: ['system.view'] },
        { nameKey: 'sidebar.system.dataCollection', path: '/system?tab=data-collection', permissions: ['system.view'] },
        { nameKey: 'sidebar.system.log', path: '/system?tab=log', permissions: ['system.view'] },
        { nameKey: 'sidebar.system.ip', path: '/system/ip', permissions: ['system.view'] },
        { nameKey: 'sidebar.system.labeling', path: '/labeling', permissions: ['system.view'] }
      ]
    },
    {
      id: 'platform',
      section: 'admin',
      icon: 'admin_panel_settings',
      titleKey: 'sidebar.platform.title',
      requiredPermissions: ['platform.view'],
      superAdminOnly: true,
      submenus: [
        { nameKey: 'sidebar.platform.companies', path: '/platform/companies', permissions: ['platform.view'] },
        { nameKey: 'sidebar.platform.users', path: '/platform/users', permissions: ['platform.view'] },
        { nameKey: 'sidebar.platform.status', path: '/platform/status', permissions: ['platform.view'] },
        { nameKey: 'sidebar.platform.licenses', path: '/platform/licenses', permissions: ['platform.view'] }
      ]
    }
  ];

  // 권한에 따라 메뉴 필터링
  const menuItems = allMenuItems.filter(menu => {
    if (menu.devOnly && !MODULES_BC_ENABLED) return false;        // 미사용 기능(모듈B/C)은 배포 빌드에서 숨김
    if (menu.superAdminOnly && !isSuperAdmin) return false;        // 슈퍼어드민 전용
    return hasMenuPermission(menu.requiredPermissions);
  }).map(menu => ({
    ...menu,
    submenus: menu.submenus.filter(submenu => hasMenuPermission(submenu.permissions))
  })).filter(menu => menu.submenus.length > 0);                     // 서브메뉴가 없으면 메뉴 자체를 숨김

  const toggleSubmenu = (menuId) => {
    setExpandedMenus(prev => (
      prev.includes(menuId) ? prev.filter(id => id !== menuId) : [...prev, menuId]
    ));
  };

  // 현재 페이지가 해당 메뉴에 속하는지 확인
  const isActiveMenu = (item) => item.submenus.some(submenu => location.pathname === submenu.path.split('?')[0]);

  // 시스템 메뉴는 한 경로를 탭으로 나눠 쓰므로 탭까지 봐야 현재 위치가 하나로 좁혀진다.
  // 탭이 없는 /system 은 화면이 general 로 열리니 그 값을 기본으로 둔다
  const isActiveSubmenu = (submenu) => {
    const [submenuPath, submenuQuery] = submenu.path.split('?');
    if (location.pathname !== submenuPath) return false;
    if (!submenuQuery) return true;
    const currentTab = new URLSearchParams(location.search).get('tab') || 'general';
    return new URLSearchParams(submenuQuery).get('tab') === currentTab;
  };

  const activeMenuId = menuItems.find(isActiveMenu)?.id || null;

  // 아이콘 바나 주소창으로 다른 그룹의 화면에 들어와도 그 그룹은 펼쳐 준다.
  // 그룹 id 가 바뀔 때만 동작하므로 사용자가 직접 접은 그룹을 도로 열지는 않는다
  useEffect(() => {
    if (!activeMenuId) return;
    setExpandedMenus(prev => (prev.includes(activeMenuId) ? prev : [...prev, activeMenuId]));
  }, [activeMenuId]);

  const logoUrl = settings.logoUrl ? sanitizeImageUrl(getFullImageUrl(settings.logoUrl)) : '';
  const userName = user?.name || user?.username || '';
  const userInitial = userName ? userName.trim().charAt(0) : '?';

  const renderGroup = (item) => {
    const isActive = activeMenuId === item.id;
    const isOpen = expandedMenus.includes(item.id);
    return (
      <div
        key={item.id}
        className="sb-group"
        onMouseEnter={() => !isExpanded && setHoveredMenu(item.id)}
        onMouseLeave={() => setHoveredMenu(null)}
      >
        <button
          type="button"
          className={`sb-group-head ${isActive ? 'active' : ''}`}
          title={isExpanded ? undefined : t(item.titleKey)}
          onClick={() => (isExpanded ? toggleSubmenu(item.id) : navigate(item.submenus[0].path))}
        >
          <span className="material-symbols-outlined sb-icon">{item.icon}</span>
          {isExpanded && (
            <>
              <span className="sb-group-label">{t(item.titleKey)}</span>
              <span className="material-symbols-outlined sb-chevron">{isOpen ? 'expand_less' : 'expand_more'}</span>
            </>
          )}
        </button>

        {/* 펼침: 하위 메뉴 목록 */}
        {isExpanded && isOpen && (
          <ul className="sb-sub">
            {item.submenus.map((submenu) => (
              <li key={submenu.path}>
                <button
                  type="button"
                  className={`sb-sub-item ${isActiveSubmenu(submenu) ? 'active' : ''}`}
                  onClick={() => navigate(submenu.path)}
                >
                  {t(submenu.nameKey)}
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* 접힘: 마우스를 올리면 하위 메뉴 툴팁 */}
        {!isExpanded && hoveredMenu === item.id && (
          <div className="sb-tooltip">
            <div className="sb-tooltip-title">{t(item.titleKey)}</div>
            {item.submenus.map((submenu) => (
              <button
                key={submenu.path}
                type="button"
                className={`sb-tooltip-item ${isActiveSubmenu(submenu) ? 'active' : ''}`}
                onClick={(e) => { e.stopPropagation(); navigate(submenu.path); }}
              >
                {t(submenu.nameKey)}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside className={`sb ${isExpanded ? 'sb-expanded' : 'sb-collapsed'}`}>
      {/* 로고 + 접기/펼치기 */}
      <div className="sb-top">
        {isExpanded && (
          <button type="button" className="sb-logo" onClick={() => navigate('/home')} title={t('common.home')}>
            {logoUrl
              ? <img src={logoUrl} alt={t('header.logoAlt')} className="sb-logo-img" />
              : <span className="sb-logo-text">SEORO</span>}
          </button>
        )}
        <button
          type="button"
          className="sb-toggle"
          onClick={onToggle}
          title={isExpanded ? t('sidebar.collapse') : t('sidebar.expand')}
          aria-label={isExpanded ? t('sidebar.collapse') : t('sidebar.expand')}
        >
          <span className="material-symbols-outlined">{isExpanded ? 'left_panel_close' : 'left_panel_open'}</span>
        </button>
      </div>

      {/* 메뉴 */}
      <nav className="sb-menu">
        {SECTIONS.map((section) => {
          const groups = menuItems.filter((m) => m.section === section.key);
          if (groups.length === 0) return null;
          return (
            <div key={section.key} className="sb-section">
              <div className="sb-section-label"><span>{t(section.labelKey)}</span></div>
              {groups.map(renderGroup)}
            </div>
          );
        })}
      </nav>

      {/* 사용자 — 누르면 마이페이지 / 로그아웃 (헤더의 계정·로그아웃 아이콘을 여기로 옮김) */}
      <div className="sb-footer" ref={userMenuRef}>
        <button type="button" className={`sb-user ${userMenuOpen ? 'open' : ''}`} onClick={() => setUserMenuOpen((v) => !v)} title={userName || t('common.account')}>
          <span className="sb-avatar" aria-hidden="true">{userInitial}</span>
          {isExpanded && <span className="sb-user-name">{userName || '—'}</span>}
        </button>
        {userMenuOpen && (
          <div className="sb-user-menu">
            <button type="button" className="sb-user-menu-item" onClick={() => { setUserMenuOpen(false); navigate('/mypage'); }}>
              <span className="material-symbols-outlined">account_circle</span>{t('sidebar.myPage')}
            </button>
            <button type="button" className="sb-user-menu-item sb-user-menu-danger" onClick={async () => { setUserMenuOpen(false); await authLogout(); navigate('/login'); }}>
              <span className="material-symbols-outlined">logout</span>{t('common.logout')}
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}

export default Sidebar;
